import React, { useState, useEffect } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Input,
  Label,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import Flatpickr from 'react-flatpickr';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import ActionButton from '../../../Components/Common/ActionButton';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { proHydroApi, proHydroAxios } from '../../../helpers/crude_api_helper';
import { formatForDisplay, formatForAPI } from '../../../utils/dateformatpickr';

const MAX_RANGE_DAYS = 30;

const getDefaultRange = () => {
  const to = new Date();
  to.setHours(23, 59, 0, 0);
  const from = new Date();
  from.setDate(from.getDate() - MAX_RANGE_DAYS);
  from.setHours(0, 0, 0, 0);
  return { from, to };
};

const combineDateTime = (datePart, timePart) => {
  const combined = new Date(datePart);
  const time = new Date(timePart);
  combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return combined;
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const endOfDay = date => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

const AuditView = () => {
  const defaultRange = getDefaultRange();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [fromTime, setFromTime] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);
  const [toTime, setToTime] = useState(defaultRange.to);
  const [appliedFrom, setAppliedFrom] = useState(defaultRange.from);
  const [appliedTo, setAppliedTo] = useState(defaultRange.to);
  const [formKey, setFormKey] = useState(0);
  const [toDateGen, setToDateGen] = useState(0);

  document.title = 'Audit Log';

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearch,
    sortField,
    sortDirection,
    appliedFrom,
    appliedTo,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortField, sortDirection, appliedFrom, appliedTo]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, pageSize: rowsPerPage };
      if (debouncedSearch) params.searchText = debouncedSearch;
      if (sortField) {
        params.sortField = sortField;
        params.sortOrder = sortDirection === 'asc' ? 'Asc' : 'Desc';
      }
      params.from = formatForAPI(appliedFrom);
      params.to = formatForAPI(appliedTo);
      const result = await proHydroApi.get('/v1/Audits', params);
      const list = Array.isArray(result?.items) ? result.items : [];
      setItems(list);
      setTotalRows(result?.totalCount || 0);
    } catch (error) {
      toast.error(`Failed to load data: ${error}`);
      setItems([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const headerLabel = label => (
    <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
      {label}
    </span>
  );

  const toPascalCase = field =>
    field ? field.charAt(0).toUpperCase() + field.slice(1) : field;

  const handleSort = (column, direction) => {
    setSortField(column.id ? toPascalCase(column.id) : null);
    setSortDirection(direction);
  };

  const handleFromDateChange = date => {
    if (!date) return;
    setFromDate(date);
    setToDate(null);
    setToDateGen(g => g + 1);
  };

  const handleApplyAdvanceSearch = () => {
    if (!toDate) return;
    setAppliedFrom(combineDateTime(fromDate, fromTime));
    setAppliedTo(combineDateTime(toDate, toTime));
    setAdvanceOpen(false);
  };

  const handleResetAdvanceSearch = () => {
    const def = getDefaultRange();
    setFromDate(def.from);
    setFromTime(def.from);
    setToDate(def.to);
    setToTime(def.to);
    setAppliedFrom(def.from);
    setAppliedTo(def.to);
    setFormKey(k => k + 1);
    setAdvanceOpen(false);
  };

  const handleExport = async () => {
    try {
      const query = selectedRows
        .map(row => `auditIds=${encodeURIComponent(row.id)}`)
        .join('&');
      const blob = await proHydroAxios.get(
        `/v1/Audits/Export${query ? `?${query}` : ''}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audits-export-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Failed to export data: ${error}`);
    }
  };

  const columns = [
    {
      id: 'created',
      name: headerLabel('Created'),
      selector: row => formatForDisplay(row.created),
      sortable: true,
      wrap: true,
    },
    {
      id: 'createdBy',
      name: headerLabel('By'),
      selector: row => row.createdBy,
      sortable: true,
      wrap: true,
    },
    {
      id: 'actionType',
      name: headerLabel('Action Type'),
      selector: row => row.actionType,
      sortable: true,
      wrap: true,
    },
    {
      id: 'entityName',
      name: headerLabel('Entity Name'),
      selector: row => row.entityName,
      sortable: true,
      wrap: true,
    },
    {
      id: 'entityId',
      name: headerLabel('Entity ID'),
      selector: row => row.entityId,
      sortable: true,
      wrap: true,
    },
    {
      id: 'fromIpAddress',
      name: headerLabel('From IP Address'),
      selector: row => row.fromIpAddress,
      sortable: true,
      wrap: true,
    },
    {
      name: (
        <span className='form-label text-uppercase fw-semibold fs-13'>
          Action
        </span>
      ),
      width: '90px',
      center: true,
      cell: row => (
        <Link
          to={`/crude/audit-log/dtl/${row.id}`}
          className='btn btn-sm btn-ghost-secondary btn-icon hy-icon-action'
          aria-label='View'
          title='View'
        >
          <i className='ri-eye-line align-middle' aria-hidden='true'></i>
        </Link>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='Audits' pageTitle='CRUDE' />

          <Row>
            <Col lg={12}>
              <Card id='AuditList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>Data Audit</h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <div className='search-box'>
                          <Input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Search...'
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                          />
                        </div>
                        <ActionButton
                          variant='secondary'
                          size='sm'
                          icon='ri-filter-3-line'
                          onClick={() => setAdvanceOpen(true)}
                        >
                          Advance Search
                        </ActionButton>
                        <ActionButton
                          variant='secondary'
                          size='sm'
                          icon='ri-download-2-line'
                          onClick={handleExport}
                        >
                          Export CSV
                          {selectedRows.length > 0
                            ? ` (${selectedRows.length})`
                            : ''}
                        </ActionButton>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div
                    className='table-responsive'
                    style={{
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={items}
                      selectableRows
                      onSelectedRowsChange={({ selectedRows: rows }) =>
                        setSelectedRows(rows)
                      }
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={rowsPerPage}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={page => setCurrentPage(page)}
                      onChangeRowsPerPage={newPerPage => {
                        setRowsPerPage(newPerPage);
                        setCurrentPage(1);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      fixedHeader={false}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No audit found
                        </div>
                      }
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                            position: 'relative',
                            overflow: 'visible',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Offcanvas
            direction='end'
            isOpen={advanceOpen}
            toggle={() => setAdvanceOpen(!advanceOpen)}
          >
            <OffcanvasHeader toggle={() => setAdvanceOpen(!advanceOpen)}>
              Advance Search
            </OffcanvasHeader>
            <OffcanvasBody>
              <div className='mb-3'>
                <Label className='form-label' for='audit-from-date'>
                  From Date
                </Label>
                <Flatpickr
                  key={`from-date-${formKey}`}
                  id='audit-from-date'
                  className='form-control'
                  options={{ dateFormat: 'd/m/Y', defaultDate: fromDate }}
                  onChange={([date]) => handleFromDateChange(date)}
                />
              </div>
              <div className='mb-3'>
                <Label className='form-label' for='audit-from-time'>
                  From Time
                </Label>
                <Flatpickr
                  key={`from-time-${formKey}`}
                  id='audit-from-time'
                  className='form-control'
                  options={{
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: 'H:i',
                    defaultDate: fromTime,
                  }}
                  onChange={([date]) => date && setFromTime(date)}
                />
              </div>
              <div className='mb-3'>
                <Label className='form-label' for='audit-to-date'>
                  To Date
                </Label>
                <Flatpickr
                  key={`to-date-${formKey}-${toDateGen}`}
                  id='audit-to-date'
                  className='form-control'
                  options={{
                    dateFormat: 'd/m/Y',
                    minDate: fromDate,
                    maxDate: endOfDay(addDays(fromDate, MAX_RANGE_DAYS)),
                    defaultDate: toDate || undefined,
                  }}
                  onChange={([date]) => date && setToDate(date)}
                />
                {!toDate && (
                  <div className='form-text text-warning'>
                    Pilih ulang To Date (maksimal {MAX_RANGE_DAYS} hari dari
                    From Date)
                  </div>
                )}
              </div>
              <div className='mb-3'>
                <Label className='form-label' for='audit-to-time'>
                  To Time
                </Label>
                <Flatpickr
                  key={`to-time-${formKey}`}
                  id='audit-to-time'
                  className='form-control'
                  options={{
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: 'H:i',
                    defaultDate: toTime,
                  }}
                  onChange={([date]) => date && setToTime(date)}
                />
              </div>
            </OffcanvasBody>
            <div className='offcanvas-footer border-top p-3 text-center hstack gap-2'>
              <ActionButton
                variant='tertiary'
                className='w-100'
                icon='ri-restart-line'
                onClick={handleResetAdvanceSearch}
              >
                Reset
              </ActionButton>
              <ActionButton
                variant='primary'
                className='w-100'
                icon='ri-check-line'
                disabled={!toDate}
                onClick={handleApplyAdvanceSearch}
              >
                Apply
              </ActionButton>
            </div>
          </Offcanvas>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AuditView;
